import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { GroupMembers } from '../GroupMembers'
import { GroupPermissions } from '../GroupPermissions'

import type { GroupDetailProps } from './GroupDetail.types'
import { Card, Flex, Heading, Tabs } from '@percent/lemonade'
import { TabPanel } from '@percent/admin-dashboard/common/components'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'

const getGroupMembersRoute = (groupId: string) => `/accounts/groups/${groupId}/members`
const getGroupPermissionsRoute = (groupId: string) => `/accounts/groups/${groupId}/permissions`

export function GroupDetail({ group, permissions }: GroupDetailProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { canViewRolesOnAnyDomain } = usePermissions()
  const [activeTab, setActiveTab] = useState('')

  useEffect(() => {
    if (location) {
      setActiveTab(location.pathname)
    }
  }, [location])

  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue)
    navigate(newValue)
  }

  const tabItems = [
    ...(permissions.can_list_members
      ? [{ key: getGroupMembersRoute(group.id), label: t('groupDetail.tabs.membersAndGroups') }]
      : []),
    ...(canViewRolesOnAnyDomain
      ? [{ key: getGroupPermissionsRoute(group.id), label: t('groupDetail.tabs.permissions') }]
      : [])
  ]

  return (
    <Card>
      <Flex direction="column" w="100%">
        <Flex direction="column" px="lg" pt="lg" pb="md">
          <Heading level="h5">{group.name}</Heading>
        </Flex>
        <Flex direction="column" px="lg">
          <Tabs activeTab={activeTab} onChangeTab={handleTabChange} items={tabItems} />
        </Flex>
        {permissions.can_list_members && (
          <TabPanel value={activeTab} index={getGroupMembersRoute(group.id)}>
            <GroupMembers groupId={group.id} groupName={group.name} permissions={permissions} />
          </TabPanel>
        )}
        {canViewRolesOnAnyDomain && (
          <TabPanel value={activeTab} index={getGroupPermissionsRoute(group.id)}>
            <GroupPermissions groupId={group.id} />
          </TabPanel>
        )}
      </Flex>
    </Card>
  )
}
